import { Link } from "@tanstack/react-router";
import type { Crumb } from "@/lib/schema";
import { BrandEyebrow, BrandXMotif } from "@/components/site/BrandXMotif";

export function PageHero({

  eyebrow,
  title,
  intro,
  tagline,
  trail,
  backgroundImage,
  backgroundMobileImage,
  backgroundAlt,
  backgroundTitle,
  backgroundDescription,
  backgroundVivid,
  backgroundPosition = "center",
  backgroundReveal,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  /** Optional brand slogan rendered above the H1 (keeps the keyword H1 intact). */
  tagline?: string;
  trail?: readonly Crumb[];
  /** Optional hero background image URL (rendered behind a dark gradient). */
  backgroundImage?: string;
  /** Optional portrait crop served only below the desktop breakpoint. */
  backgroundMobileImage?: string;
  backgroundAlt?: string;
  backgroundTitle?: string;
  backgroundDescription?: string;
  /** Let the background image's colours show through more (lighter overlay). */
  backgroundVivid?: boolean;
  /** Anchor the crop to the centre or the lower edge of the source image. */
  backgroundPosition?: "center" | "bottom";
  /** Use the lightest readable overlay for imagery that should remain prominent. */
  backgroundReveal?: boolean;
}) {
  void backgroundImage;
  void backgroundMobileImage;
  void backgroundAlt;
  void backgroundTitle;
  void backgroundVivid;
  void backgroundPosition;
  void backgroundReveal;
  return (
    <section data-dark-hero className="relative isolate min-h-[380px] overflow-hidden border-b border-night-border bg-night text-night-foreground md:min-h-[420px]">
      <BrandXMotif variant="dark" size="subpage" className="pointer-events-none absolute right-[-64px] top-[30px] hidden md:block" />
      <BrandXMotif variant="dark" size="subpage" className="pointer-events-none absolute right-[-72px] top-3 block h-[250px] w-[250px] opacity-45 md:hidden" />
      <div className="section-shell relative z-10 py-14 md:py-16">
        {backgroundDescription ? <p className="sr-only">{backgroundDescription}</p> : null}
        {trail && <Breadcrumbs trail={trail} />}
        <BrandEyebrow>{eyebrow}</BrandEyebrow>
        {tagline && (
          <p className="mt-4 max-w-3xl text-xl font-extrabold leading-snug text-night-foreground md:text-2xl">{tagline}</p>
        )}
        <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] text-night-foreground md:text-[52px]">
          {title}
        </h1>
        <p className="mt-5 max-w-[660px] text-lg leading-relaxed text-night-muted md:text-[19px]">{intro}</p>
      </div>
    </section>
  );
}



/** Visible breadcrumbs; must stay in sync with the route's BreadcrumbList JSON-LD (both from TRAILS). */
export function Breadcrumbs({ trail }: { trail: readonly Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6" data-breadcrumb>
        <ol className="flex flex-wrap items-center gap-2 text-xs font-semibold tracking-wide text-night-muted">
        {trail.map((crumb, i) => {
          const isLast = i === trail.length - 1;
          return (
            <li key={crumb.path} className="flex items-center gap-2">
              {isLast ? (
                <span aria-current="page" className="font-bold text-night-foreground" data-breadcrumb-item>
                  {crumb.name}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  // Two-tone focus indicator: light inner ring for dark surfaces,
                  // dark outer outline for light surfaces (both >= 3:1).
                    className="rounded-sm underline-offset-4 transition-colors hover:text-night-foreground focus-visible:underline focus-visible:text-night-foreground focus-visible:ring-2 focus-visible:ring-night-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink"
                  data-breadcrumb-item
                >
                  {crumb.name}
                </Link>
              )}
              {!isLast && (
                <span aria-hidden="true" className="text-night-muted/70">
                  /
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Light-background variant of the breadcrumb bar for detail pages.
 *  Renders as a full-width section so it can be dropped directly after `<PageHero />`. */
export function BreadcrumbBar({ trail }: { trail: readonly Crumb[] }) {
  return (
    <section className="section-shell bg-background py-2">
      <nav aria-label="Breadcrumb" className="border-b border-border py-3" data-breadcrumb="light">
        <ol className="flex flex-wrap items-center gap-2 text-xs font-semibold tracking-wide text-muted-foreground">
          {trail.map((crumb, i) => {
            const isLast = i === trail.length - 1;
            return (
              <li key={crumb.path} className="flex items-center gap-2">
                {isLast ? (
                  <span aria-current="page" className="font-bold text-foreground" data-breadcrumb-item>
                    {crumb.name}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="rounded-sm underline-offset-4 transition-colors text-primary hover:text-foreground hover:underline focus-visible:underline focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink"
                    data-breadcrumb-item
                  >
                    {crumb.name}
                  </Link>
                )}
                {!isLast && (
                  <span aria-hidden="true" className="text-muted-foreground/70">
                    /
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </section>
  );
}

/** Table of contents with anchor links for long detail pages. */
export function TableOfContents({ items }: { items: Array<{ id: string; label: string }> }) {
  return (
    <nav aria-label="Table of contents" className="rounded-xl border border-border bg-card p-6 shadow-card" data-toc>
      <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">On this page</h2>
      <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, i) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className="group flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink"
              data-toc-item
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-bold text-secondary-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="underline-offset-4 group-hover:underline">{item.label}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}






export function SectionHeading({
  eyebrow,
  title,
  lead,
  titleId,
}: {
  eyebrow: string;
  title: string;
  lead?: string;
  /** Optional id on the <h2> so the wrapping section can use aria-labelledby. */
  titleId?: string;
}) {
  return (
    <div className="max-w-3xl">
      <p className="eyebrow">
        <span aria-hidden="true">— </span>
        {eyebrow}
      </p>
      <h2 id={titleId} className="mt-3 text-3xl font-bold md:text-4xl">
        {title}
      </h2>
      {lead && <p className="mt-4 text-base text-muted-foreground md:text-lg">{lead}</p>}
    </div>
  );
}


export function FeatureCard({
  title,
  children,
  index,
}: {
  title: string;
  children: React.ReactNode;
  index?: string;
}) {
  return (
    <article className="group rounded-lg border border-border bg-card p-6 shadow-card transition-transform duration-300 hover:-translate-y-1">
      {index && (
        <span className="font-display text-xs font-bold tracking-[0.2em] text-highlight">
          {index}
        </span>
      )}
      <h3 className="mt-2 text-lg font-bold">{title}</h3>
      <div className="mt-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </article>
  );
}
